import { FontUtils, GridUtils, Image3D, ImgButton, ImgToggleButton, InputUtils, LayoutUtils, MeshUtils, ObjectBuilder, Rectangle3D, RichTextBlock3D, RichTextGrammar, ScrollViewer3D, TabControl, Validator } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { createVector3 } from "../ButtonAnimation";
import { Keyboard3D, TextKeyboardLayout } from "./keyboard/Keyboard3D";
import { AppView } from "../AppView";
import { MeshName } from "../MeshName";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { text } from "../../TextResources";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { XRInputHelper } from "../XRInputHelper";
import { isSuccessAuthCallbackResponse } from "../../Authorization";
import { Ellipse } from "@babylonjs/gui/2D/controls/ellipse.js";
import { ScrollHandler } from "../ScrollHandler";
export class LoginDialog {
    get mesh2D() {
        return this._mesh2D;
    }
    constructor(parent, callbacks) {
        this._currentTab = 0;
        this._inputs = [[], []];
        this._backActions = [];
        this._animationInterval = null;
        this.show = (tab) => {
            if (this._callbacks.onShowCallback) {
                this._callbacks.onShowCallback();
            }
            MeshUtils.show(this._mesh2D);
            if (tab !== undefined) {
                this._tabControl.currentTab = tab === "signUp" ? 1 : 0; //Hack to ensure the state change TODO: fix in babylon-tools
                this._tabControl.currentTab = tab === "signUp" ? 0 : 1;
            }
            if (this._backCallback)
                this._backActions.push(() => {
                    this.hide();
                    this._backCallback();
                });
            this._backButton.isVisible = this._backActions.length > 0;
            if (this._animationInterval === null)
                this._animationInterval = setInterval(() => this._animationFunc(), Env.lobby.login.loader.interval);
            this._scrollHandler.addThumbstickAxesChangedListener();
        };
        this.hide = () => {
            if (MeshUtils.isHidden(this._mesh2D))
                return;
            MeshUtils.hide(this._mesh2D);
            this._keyboard.isVisible = false;
            while (this._backActions.length > (this._backCallback === undefined ? 0 : 1))
                this._backActions.pop()();
            if (this._animationInterval !== null)
                clearInterval(this._animationInterval);
            this._animationInterval = null;
            this._scrollHandler.releaseThumbstickAxesChangedListener();
            if (this._callbacks.onHideCallback) {
                this._callbacks.onHideCallback();
            }
        };
        this._close = () => {
            this.hide();
            if (this._callbacks.closeCallback) {
                this._callbacks.closeCallback();
            }
        };
        this._callbacks = callbacks;
        const ls = Env.lobby.login;
        const appView = AppView.getInstance();
        this._mesh2D = MeshUtils.createPlaneMesh(MeshName.loginForm, ls.background.width, ls.background.height, appView.scene, parent ?? appView.guiRootNode, DcRenderingGroup.messageDialogs);
        this._mesh2D.mesh.position = createVector3(Env.lobby.login.position);
        this._scrollHandler = new ScrollHandler([MeshName.loginForm], this._mesh2D.m2px(ls.legalTextContainer.speed), (v) => {
            if (this._policyScrollViewer.isVisible)
                this._policyScrollViewer.verticalValue = Math.max(Math.min(this._policyScrollViewer.verticalValue + v / (this._policyScrollViewer.children[0].heightInPixels - this._policyScrollViewer.heightInPixels), 1), 0);
            else if (this._termsScrollViewer.isVisible)
                this._termsScrollViewer.verticalValue = Math.max(Math.min(this._termsScrollViewer.verticalValue + v / (this._termsScrollViewer.children[0].heightInPixels - this._termsScrollViewer.heightInPixels), 1), 0);
        });
        const pointerAddon = XRInputHelper.addPointerAddon(this._mesh2D.mesh);
        pointerAddon.onPointerOut.add(() => {
            this._mesh2D.notifyChildrenOnPointerOut();
            this._scrollHandler.stopScroll();
        });
        const container = new Rectangle3D("loginDialogContainer");
        container.cornerRadius = this._mesh2D.m2px(ls.background.cornerRadius);
        container.background = ls.background.backgroundColor;
        container.thickness = 0;
        this._mesh2D.texture.addControl(container);
        this._nextCallback = () => {
            this._keyboard.isVisible = false;
            let focusedControl = container.host.focusedControl;
            if (focusedControl === null) {
                for (const i of this._inputs[this._currentTab]) {
                    if (i.keepsFocusWith()?.map(c => c.name).includes(MeshName.loginFormKeyboard)) {
                        this._keyboard.control.disconnect(i);
                        i.disconnectControl(this._keyboard.rootControl);
                        this._keyboard.isVisible = false;
                        break;
                    }
                }
                return;
            }
            const idx = this._inputs[this._currentTab].indexOf(focusedControl);
            if (idx === -1)
                return;
            this._inputs[this._currentTab][idx].blur();
            if (idx < this._inputs[this._currentTab].length - 1)
                this._inputs[this._currentTab][idx + 1].focus();
        };
        this._keyboard = new Keyboard3D(MeshName.loginFormKeyboard, appView.scene, parent ?? appView.guiRootNode, TextKeyboardLayout.login);
        this._keyboard.mesh.position = createVector3(ls.keyboard.position);
        this._keyboard.control.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        this._keyboard.isVisible = false;
        this._keyboard.onEnterButtonClickedObservable.add(() => { this._nextCallback(); });
        const mainGrid = GridUtils.CreateGrid("loginDialogMainGrid", ls.mainGrid, this._mesh2D);
        container.addControl(mainGrid);
        const headerGrid = GridUtils.CreateGrid("loginDialogHeaderGrid", ls.headerGrid, this._mesh2D);
        headerGrid.background = ls.headerGrid.background;
        mainGrid.addControl(headerGrid);
        this._backCallback = callbacks.backCallback;
        this._backButton = new ImgButton("loginDialogBackButton", ls.back);
        this._backButton.widthInPixels = this._mesh2D.m2px(ls.back.size + ls.back.padding);
        this._backButton.heightInPixels = this._mesh2D.m2px(ls.back.size + ls.back.padding);
        this._backButton.paddingTopInPixels = this._mesh2D.m2px(ls.back.padding);
        this._backButton.paddingLeftInPixels = this._mesh2D.m2px(ls.back.padding);
        this._backButton.paddingBottomInPixels = this._mesh2D.m2px(ls.back.padding);
        this._backButton.onPointerClickObservable.add(() => {
            if (this._backActions.length > 0)
                this._backActions.pop()();
            this._backButton.isVisible = this._backActions.length > 0;
        });
        headerGrid.addControl(this._backButton);
        this._backButton.isVisible = this._backActions.length > 0;
        this._headerText = ObjectBuilder.createTextBlock3D(this._mesh2D, "loginDialogHeaderText", text.loginDialog_header, ls.header);
        headerGrid.addControl(this._headerText, 0, 1);
        const closeButton = new ImgButton("loginDialogCloseButton", ls.close);
        closeButton.widthInPixels = this._mesh2D.m2px(ls.close.size + ls.close.padding);
        closeButton.heightInPixels = this._mesh2D.m2px(ls.close.size + ls.close.padding);
        closeButton.paddingTopInPixels = this._mesh2D.m2px(ls.close.padding);
        closeButton.paddingRightInPixels = this._mesh2D.m2px(ls.close.padding);
        closeButton.paddingBottomInPixels = this._mesh2D.m2px(ls.close.padding);
        closeButton.onPointerClickObservable.add(() => {
            setTimeout(() => this._close(), 0);
        });
        headerGrid.addControl(closeButton, 0, 2);
        const spinnerScreen = new StackPanel("loginDialogSpinnerContainer");
        const logo = new Image3D("loginDialogLoaderLogo", ls.loader.logo);
        logo.widthInPixels = this._mesh2D.m2px(ls.loader.width);
        logo.heightInPixels = this._mesh2D.m2px(ls.loader.height);
        spinnerScreen.addControl(logo);
        const animationPanel = new StackPanel("loginDialogAnimationStackPanel");
        animationPanel.isVertical = false;
        animationPanel.adaptHeightToChildren = true;
        animationPanel.widthInPixels = this._mesh2D.m2px(ls.loader.width);
        animationPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        animationPanel.spacing = this._mesh2D.m2px(ls.loader.dot.spacing);
        animationPanel.paddingLeftInPixels = this._mesh2D.m2px(ls.loader.dot.padding.left);
        animationPanel.paddingTopInPixels = this._mesh2D.m2px(ls.loader.dot.padding.top);
        const dots = [];
        let activeDot = 0;
        for (let i = 0; i < ls.loader.dot.number; i++) {
            const dot = new Ellipse();
            dot.widthInPixels = this._mesh2D.m2px(ls.loader.dot.diameter);
            dot.heightInPixels = this._mesh2D.m2px(ls.loader.dot.diameter);
            dot.background = i === activeDot ? ls.loader.dot.activeColor : ls.loader.dot.inactiveColor;
            dot.thickness = 0;
            animationPanel.addControl(dot);
            dots.push(dot);
        }
        this._animationFunc = () => {
            activeDot = (activeDot + 1) % 3;
            dots.forEach((dot, i) => {
                dot.background = i === activeDot ? ls.loader.dot.activeColor : ls.loader.dot.inactiveColor;
            });
        };
        spinnerScreen.addControl(animationPanel);
        mainGrid.addControl(spinnerScreen, 1);
        spinnerScreen.isVisible = false;
        const successScreen = new StackPanel("loginDialogSuccessContainer");
        mainGrid.addControl(successScreen, 1);
        successScreen.isVisible = false;
        this._termsScrollViewer = new ScrollViewer3D("loginDialogTermsContainer");
        this._termsScrollViewer.thickness = 0;
        this._termsScrollViewer.paddingLeftInPixels = this._mesh2D.m2px(ls.legalTextContainer.padding);
        this._termsScrollViewer.paddingTopInPixels = this._mesh2D.m2px(ls.legalTextContainer.padding);
        this._termsScrollViewer.paddingRightInPixels = this._mesh2D.m2px(ls.legalTextContainer.padding);
        this._termsScrollViewer.paddingBottomInPixels = this._mesh2D.m2px(ls.legalTextContainer.padding);
        mainGrid.addControl(this._termsScrollViewer, 1);
        this._termsScrollViewer.isVisible = false;
        const termsText = new Image3D("loginDialogTermsText", ls.legalTextContainer.terms.image);
        termsText.widthInPixels = this._mesh2D.m2px(ls.legalTextContainer.terms.width);
        termsText.heightInPixels = this._mesh2D.m2px(ls.legalTextContainer.terms.height);
        this._termsScrollViewer.addControl(termsText);
        this._policyScrollViewer = new ScrollViewer3D("loginDialogPolicyContainer");
        this._policyScrollViewer.thickness = 0;
        this._policyScrollViewer.paddingLeftInPixels = this._mesh2D.m2px(ls.legalTextContainer.padding);
        this._policyScrollViewer.paddingTopInPixels = this._mesh2D.m2px(ls.legalTextContainer.padding);
        this._policyScrollViewer.paddingRightInPixels = this._mesh2D.m2px(ls.legalTextContainer.padding);
        this._policyScrollViewer.paddingBottomInPixels = this._mesh2D.m2px(ls.legalTextContainer.padding);
        mainGrid.addControl(this._policyScrollViewer, 1);
        this._policyScrollViewer.isVisible = false;
        const policyText = new Image3D("loginDialogPolicyText", ls.legalTextContainer.policy.image);
        policyText.widthInPixels = this._mesh2D.m2px(ls.legalTextContainer.policy.width);
        policyText.heightInPixels = this._mesh2D.m2px(ls.legalTextContainer.policy.height);
        this._policyScrollViewer.addControl(policyText);
        const successImage = new Image3D("loginDialogAuthSuccessImage", ls.successScreen.icon.image);
        successImage.widthInPixels = this._mesh2D.m2px(ls.successScreen.icon.width);
        successImage.heightInPixels = this._mesh2D.m2px(ls.successScreen.icon.height);
        successScreen.addControl(successImage);
        const successText = ObjectBuilder.createTextBlock3D(this._mesh2D, "loginDialogAuthSuccessText", text.loginDialog_authSuccess_signUp, ls.successScreen.textLayout);
        successScreen.addControl(successText);
        const signUpGrid = this._createSignUpPanel(callbacks.signupCallback, successScreen, spinnerScreen, this._termsScrollViewer, this._policyScrollViewer);
        const signInGrid = this._createSignInPanel(callbacks.signInCallback, successScreen, spinnerScreen);
        const signUpText = ObjectBuilder.createTextBlock3D(this._mesh2D, "loginDialogSignUpTabText", text.loginDialog_signUp, ls.modeSelector.textLayout.default);
        const signInText = ObjectBuilder.createTextBlock3D(this._mesh2D, "loginDialogSignInTabText", text.loginDialog_signIn, ls.modeSelector.textLayout.default);
        signUpText.stateController.setStates(ls.modeSelector.textLayout.states.map(s => { return { state: s.state, style: FontUtils.getAdjustedFontStyle(s.style, this._mesh2D) }; }));
        signInText.stateController.setStates(ls.modeSelector.textLayout.states.map(s => { return { state: s.state, style: FontUtils.getAdjustedFontStyle(s.style, this._mesh2D) }; }));
        this._tabControl = new TabControl("loginDialogTabControl");
        const buttonLayout = LayoutUtils.GetButtonLayout(ls.modeSelector.imageLayout, this._mesh2D);
        this._tabControl.addControl(signUpGrid, signUpText, buttonLayout);
        this._tabControl.addControl(signInGrid, signInText, buttonLayout);
        this._tabControl.onTabChangedObservable.add((t) => {
            this._currentTab = t;
            successText.text = t === 0 ? text.loginDialog_authSuccess_signUp : text.loginDialog_authSuccess_signIn;
        });
        mainGrid.addControl(this._tabControl, 1);
    }
    createInput(name, placeholder, validatorFuncs, icon, onTextChanged, validationResults, tab, isPassword = false) {
        const ls = Env.lobby.login;
        const inputErrorStackPanel = new StackPanel();
        inputErrorStackPanel.leftInPixels = this._mesh2D.m2px(ls.input.left);
        const inputStackPanel = new StackPanel();
        inputStackPanel.isVertical = false;
        inputStackPanel.adaptHeightToChildren = true;
        inputStackPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        inputErrorStackPanel.addControl(inputStackPanel);
        const errorText = ObjectBuilder.createTextBlock3D(this._mesh2D, `${name}ErrorText`, "", ls.input.errorText);
        inputErrorStackPanel.addControl(errorText);
        const input = InputUtils.CreateInput(name, ls.input.layout, this._mesh2D);
        input.isPassword = isPassword;
        if (input.isPassword)
            input.contentPadding = { horizontal: this._mesh2D.m2px(ls.input.layout.contentPadding.left) };
        input.placeholder = placeholder;
        input.cursor.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        this._inputs[tab].push(input);
        input.onTextChangedObservable.add(() => {
            if (input.text.includes('\n')) {
                input.text = input.text.replace('\n', '');
                this._nextCallback();
                return;
            }
            onTextChanged(input.text);
        });
        input.onPointerDownObservable.add(() => {
            input.focus();
        });
        input.onFocusObservable.add(() => {
            this._keyboard.control.connect(input);
            input.connectControl(this._keyboard.rootControl);
            this._keyboard.isVisible = true;
            const y = this._mesh2D.mesh.position.y + this._mesh2D.height / 2 - this._keyboard.height / 2 - this._mesh2D.px2m(input._currentMeasure.top + input._currentMeasure.height) - ls.input.keyboardOffset;
            this._keyboard.mesh.position = new Vector3(ls.keyboard.position.x, y, ls.keyboard.position.z);
        });
        input.onBlurObservable.add(() => {
            this._keyboard.control.disconnect(input);
            input.disconnectControl(this._keyboard.rootControl);
            this._keyboard.isVisible = false;
        });
        inputStackPanel.addControl(input);
        const validationSuccessImage = new Image3D(`${name}ValidationSuccess`, ls.validationIcons.success);
        validationSuccessImage.widthInPixels = this._mesh2D.m2px(ls.validationIcons.width + ls.validationIcons.padding);
        validationSuccessImage.heightInPixels = this._mesh2D.m2px(ls.validationIcons.height);
        validationSuccessImage.paddingLeftInPixels = this._mesh2D.m2px(ls.validationIcons.padding);
        validationSuccessImage.isVisible = false;
        inputStackPanel.addControl(validationSuccessImage);
        const validationFailImage = new Image3D(`${name}ValidationFail`, ls.validationIcons.fail);
        validationFailImage.widthInPixels = this._mesh2D.m2px(ls.validationIcons.width + ls.validationIcons.padding);
        validationFailImage.heightInPixels = this._mesh2D.m2px(ls.validationIcons.height);
        validationFailImage.paddingLeftInPixels = this._mesh2D.m2px(ls.validationIcons.padding);
        validationFailImage.isVisible = false;
        inputStackPanel.addControl(validationFailImage);
        validationResults[name] = [];
        const validators = [];
        for (let i = 0; i < validatorFuncs.length; i++) {
            const valFuncText = validatorFuncs[i];
            validationResults[name].push(false);
            const validator = new Validator(input, valFuncText.func, [input.onBlurObservable]);
            validators.push(validator);
            validator.onValidationObservable.add((v) => {
                validationResults[name][i] = v ?? false;
                const result = validationResults[name].every(v => v);
                if (result)
                    errorText.text = "";
                else if (!v) {
                    if (typeof (valFuncText.errorText) === "string")
                        errorText.text = valFuncText.errorText;
                    else
                        errorText.text = valFuncText.errorText();
                }
                input.stateController.changeState(result ? "normal" : "invalid");
                validationSuccessImage.isVisible = result;
                validationFailImage.isVisible = !result;
            });
        }
        this._mesh2D.onIsVisibleChanged.add((v) => {
            if (!v) {
                errorText.text = "";
                validationSuccessImage.isVisible = false;
                validationFailImage.isVisible = false;
                input.text = "";
                input.stateController.changeState("normal");
            }
        });
        const image = new Image3D(`${name}Icon`, icon);
        image.widthInPixels = this._mesh2D.m2px(ls.icons.width);
        image.heightInPixels = this._mesh2D.m2px(ls.icons.height);
        image.leftInPixels = this._mesh2D.m2px(ls.icons.padding);
        image.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        input.addControl(image);
        if (isPassword) {
            const showPasswordButton = new ImgToggleButton(`${name}ShowPasswordButton`, LayoutUtils.GetButtonLayout(ls.icons.showPasswordButton, this._mesh2D));
            showPasswordButton.widthInPixels = this._mesh2D.m2px(ls.icons.width + ls.icons.padding);
            showPasswordButton.heightInPixels = this._mesh2D.m2px(ls.icons.height);
            showPasswordButton.paddingRightInPixels = this._mesh2D.m2px(ls.icons.padding);
            showPasswordButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
            showPasswordButton.isFocusInvisible = true;
            showPasswordButton.isPointerBlocker = true;
            showPasswordButton.onIsToggledChangedObservable.add((v) => {
                input.isPassword = !v;
            });
            input.addControl(showPasswordButton);
            const showPasswordImage = new Image3D(`${name}ShowPasswordIcon`, ls.icons.showPassword);
            showPasswordImage.widthInPixels = this._mesh2D.m2px(ls.icons.width);
            showPasswordImage.heightInPixels = this._mesh2D.m2px(ls.icons.height);
            showPasswordButton.addControl(showPasswordImage);
        }
        return { inputStackPanel: inputErrorStackPanel, validators, input };
    }
    ;
    _createSignUpPanel(callback, successScreen, spinnerScreen, termsScreen, policyScreen) {
        const ls = Env.lobby.login;
        const signUpGrid = GridUtils.CreateGrid("loginDialogSignupGrid", ls.signUpsignInPanel.gridLayout, this._mesh2D);
        const signUpStackPanel = new StackPanel("loginDialogSignupStackPanel");
        signUpStackPanel.top = this._mesh2D.m2px(ls.signUpsignInPanel.top);
        signUpStackPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        signUpGrid.addControl(signUpStackPanel);
        const signUpButton = new ImgButton("loginDialogSignUpButton", LayoutUtils.GetButtonLayout(ls.signUpSignInButton, this._mesh2D));
        let email = "";
        let username = "";
        let password = "";
        const emailBackendErrors = [];
        const usernameBackendErrors = [];
        const passwordBackendErrors = [];
        let backendError = null;
        const validationResults = {};
        const emailInput = this.createInput("loginDialogSignUpEmailInput", text.loginDialog_inputPlaceholders_email, [
            ...ls.validationRules.email.map((v, i) => { return { func: (input) => v(input.text), errorText: text.loginDialog_validationTexts_email[i] }; }),
            { func: () => emailBackendErrors.length === 0, errorText: () => emailBackendErrors[0] },
            { func: () => backendError === null, errorText: () => backendError }
        ], ls.icons.email, t => email = t, validationResults, 0);
        emailInput.input.onFocusObservable.add(() => {
            emailBackendErrors.splice(0, emailBackendErrors.length);
            backendError = null;
        });
        signUpStackPanel.addControl(emailInput.inputStackPanel);
        const usernameInput = this.createInput("loginDialogSignUpUsernameInput", text.loginDialog_inputPlaceholders_username, [
            ...ls.validationRules.username.map((v, i) => { return { func: (input) => v(input.text), errorText: text.loginDialog_validationTexts_username[i] }; }),
            { func: () => usernameBackendErrors.length === 0, errorText: () => usernameBackendErrors[0] },
        ], ls.icons.username, t => username = t, validationResults, 0);
        usernameInput.input.onFocusObservable.add(() => {
            usernameBackendErrors.splice(0, usernameBackendErrors.length);
            backendError = null;
            emailInput.validators.forEach(v => v.decorate());
        });
        signUpStackPanel.addControl(usernameInput.inputStackPanel);
        const passwordInput = this.createInput("loginDialogSignUpPasswordInput", text.loginDialog_inputPlaceholders_password, [
            { func: (i) => ls.validationRules.passwordContains(i.text, email), errorText: text.loginDialog_validationTexts_passwordSpecial_containsEmail },
            { func: (i) => ls.validationRules.passwordContains(i.text, username), errorText: text.loginDialog_validationTexts_passwordSpecial_containsUsername },
            ...ls.validationRules.password.map((v, idx) => { return { func: (i) => v(i.text), errorText: text.loginDialog_validationTexts_password[idx] }; }),
            { func: () => passwordBackendErrors.length === 0, errorText: () => passwordBackendErrors[0] },
        ], ls.icons.password, t => password = t, validationResults, 0, true);
        passwordInput.input.onFocusObservable.add(() => {
            passwordBackendErrors.splice(0, passwordBackendErrors.length);
            backendError = null;
            emailInput.validators.forEach(v => v.decorate());
        });
        signUpStackPanel.addControl(passwordInput.inputStackPanel);
        const userAgreementStackPanel = new StackPanel();
        userAgreementStackPanel.isVertical = false;
        userAgreementStackPanel.adaptHeightToChildren = true;
        userAgreementStackPanel.leftInPixels = this._mesh2D.m2px(ls.input.left);
        userAgreementStackPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        signUpStackPanel.addControl(userAgreementStackPanel);
        const userAgreementContainer = new Rectangle3D("loginDialogUserAgreementContainer");
        userAgreementContainer.background = ls.input.backgroundColor;
        userAgreementContainer.cornerRadius = this._mesh2D.m2px(ls.input.layout.backgroundLayout.cornerRadius);
        userAgreementContainer.widthInPixels = this._mesh2D.m2px(ls.userAgreement.width);
        userAgreementContainer.heightInPixels = this._mesh2D.m2px(ls.userAgreement.height);
        userAgreementContainer.thickness = 0;
        userAgreementStackPanel.addControl(userAgreementContainer);
        const userAgreementPanel = new StackPanel("loginDialogUserAgreementPanel");
        userAgreementPanel.isVertical = false;
        userAgreementPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        userAgreementPanel.spacing = this._mesh2D.m2px(ls.userAgreement.spacing);
        userAgreementContainer.addControl(userAgreementPanel);
        const userAgreementButton = new ImgToggleButton("loginDialogUserAgreementButton", ls.userAgreement.checkbox);
        userAgreementButton.widthInPixels = this._mesh2D.m2px(ls.userAgreement.checkbox.width + ls.userAgreement.checkbox.padding.left);
        userAgreementButton.heightInPixels = this._mesh2D.m2px(ls.userAgreement.checkbox.height);
        userAgreementButton.paddingLeftInPixels = this._mesh2D.m2px(ls.userAgreement.checkbox.padding.left);
        userAgreementPanel.addControl(userAgreementButton);
        const userAgreementText = new RichTextBlock3D(RichTextGrammar.RTTag, "loginDialogUserAgreementText", text.loginDialog_userAgreement);
        FontUtils.setTextBlockStyle(userAgreementText, ls.userAgreement.text, this._mesh2D);
        userAgreementText.onHyperlinkEnterObservable.add(() => {
            XRInputHelper.getInstance()?.changePointerState(true);
        });
        userAgreementText.onHyperlinkLeaveObservable.add(() => {
            XRInputHelper.getInstance()?.changePointerState(false);
        });
        userAgreementText.onHyperlinkClickedObservable.add((l) => {
            if (l === "https://terms") {
                termsScreen.isVisible = true;
                policyScreen.isVisible = false;
                termsScreen.verticalValue = 0;
                this._headerText.text = text.loginDialog_termsHeader;
            }
            else if (l === "https://policy") {
                termsScreen.isVisible = false;
                policyScreen.isVisible = true;
                policyScreen.verticalValue = 0;
                this._headerText.text = text.loginDialog_policyHeader;
            }
            this._tabControl.isVisible = false;
            this._backActions.push(() => {
                this._tabControl.isVisible = true;
                termsScreen.isVisible = false;
                policyScreen.isVisible = false;
                this._headerText.text = text.loginDialog_header;
            });
            this._backButton.isVisible = true;
        });
        userAgreementPanel.addControl(userAgreementText);
        const validationSuccessImage = new Image3D(`loginDialogUserAgreementValidationSuccess`, ls.validationIcons.success);
        validationSuccessImage.widthInPixels = this._mesh2D.m2px(ls.validationIcons.width + ls.validationIcons.padding);
        validationSuccessImage.heightInPixels = this._mesh2D.m2px(ls.validationIcons.height);
        validationSuccessImage.paddingLeftInPixels = this._mesh2D.m2px(ls.validationIcons.padding);
        validationSuccessImage.isVisible = false;
        userAgreementStackPanel.addControl(validationSuccessImage);
        const validationFailImage = new Image3D(`loginDialogUserAgreementValidationFail`, ls.validationIcons.fail);
        validationFailImage.widthInPixels = this._mesh2D.m2px(ls.validationIcons.width + ls.validationIcons.padding);
        validationFailImage.heightInPixels = this._mesh2D.m2px(ls.validationIcons.height);
        validationFailImage.paddingLeftInPixels = this._mesh2D.m2px(ls.validationIcons.padding);
        validationFailImage.isVisible = false;
        userAgreementStackPanel.addControl(validationFailImage);
        const userAgreementValidator = new Validator(userAgreementButton, (t) => t.isToggled, [userAgreementButton.onIsToggledChangedObservable]);
        validationResults["loginDialogUserAgreement"] = [];
        validationResults["loginDialogUserAgreement"].push(false);
        userAgreementValidator.onValidationObservable.add(v => {
            userAgreementContainer.background = v ? ls.input.backgroundColor : ls.input.invalidBackgroundColor;
            validationSuccessImage.isVisible = v === true;
            validationFailImage.isVisible = v === false;
            validationResults["loginDialogUserAgreement"][0] = v ?? false;
        });
        signUpButton.widthInPixels = this._mesh2D.m2px(ls.signUpSignInButton.width);
        signUpButton.heightInPixels = this._mesh2D.m2px(ls.signUpSignInButton.height + ls.signUpSignInButton.padding);
        signUpButton.paddingBottomInPixels = this._mesh2D.m2px(ls.signUpSignInButton.padding);
        signUpButton.addControl(ObjectBuilder.createTextBlock3D(this._mesh2D, "loginDialogSignUpButtonText", text.loginDialog_signUp, ls.signUpSignInButton.text));
        signUpButton.onPointerClickObservable.add(async () => {
            if (Object.keys(validationResults).some((n) => validationResults[n].some(v => !v))) {
                emailInput.validators.forEach(v => v.decorate());
                usernameInput.validators.forEach(v => v.decorate());
                passwordInput.validators.forEach(v => v.decorate());
                userAgreementValidator.decorate();
                return;
            }
            this._tabControl.isVisible = false;
            if (spinnerScreen !== undefined)
                spinnerScreen.isVisible = true;
            const result = await Promise.race([callback(email, username, password), new Promise((success) => setTimeout(() => success({ status: "error", message: "Timeout" }), ls.timeout))]);
            if (spinnerScreen !== undefined)
                spinnerScreen.isVisible = false;
            if (isSuccessAuthCallbackResponse(result)) {
                successScreen.isVisible = true;
                setTimeout(() => {
                    this._close();
                    successScreen.isVisible = false;
                    this._tabControl.isVisible = true;
                    emailInput.input.text = "";
                    usernameInput.input.text = "";
                    passwordInput.input.text = "";
                    userAgreementButton.isToggled = false;
                }, 3000);
            }
            else {
                if (result.errors) {
                    for (const e of result.errors.filter(e => e.field === "email"))
                        emailBackendErrors.push(e.error);
                    for (const e of result.errors.filter(e => e.field === "username"))
                        usernameBackendErrors.push(e.error);
                    for (const e of result.errors.filter(e => e.field === "password"))
                        passwordBackendErrors.push(e.error);
                }
                backendError = result.message;
                this._tabControl.isVisible = true;
                emailInput.validators.forEach(v => v.decorate());
                usernameInput.validators.forEach(v => v.decorate());
                passwordInput.validators.forEach(v => v.decorate());
                userAgreementValidator.decorate();
            }
        });
        signUpGrid.addControl(signUpButton, 1);
        return signUpGrid;
    }
    _createSignInPanel(callback, successScreen, spinnerScreen) {
        const ls = Env.lobby.login;
        const signInGrid = GridUtils.CreateGrid("loginDialogSignInGrid", ls.signUpsignInPanel.gridLayout, this._mesh2D);
        const signInStackPanel = new StackPanel("loginDialogSignInStackPanel");
        signInStackPanel.top = this._mesh2D.m2px(ls.signUpsignInPanel.top);
        signInStackPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        signInGrid.addControl(signInStackPanel);
        const signInButton = new ImgButton("loginDialogSignInButton", LayoutUtils.GetButtonLayout(ls.signUpSignInButton, this._mesh2D));
        let email = "";
        let password = "";
        const emailBackendErrors = [];
        const passwordBackendErrors = [];
        let backendError = null;
        const validationResults = {};
        const emailInput = this.createInput("loginDialogSignInEmailInput", text.loginDialog_inputPlaceholders_email, [
            ...ls.validationRules.email.map((v, i) => { return { func: (input) => v(input.text), errorText: text.loginDialog_validationTexts_email[i] }; }),
            { func: () => emailBackendErrors.length === 0, errorText: () => emailBackendErrors[0] },
            { func: () => backendError === null, errorText: () => backendError }
        ], ls.icons.email, t => email = t, validationResults, 1);
        emailInput.input.onFocusObservable.add(() => {
            emailBackendErrors.splice(0, emailBackendErrors.length);
            backendError = null;
        });
        signInStackPanel.addControl(emailInput.inputStackPanel);
        const passwordInput = this.createInput("loginDialogSignInPasswordInput", text.loginDialog_inputPlaceholders_password, [
            ...ls.validationRules.password.map((v, i) => { return { func: (input) => v(input.text), errorText: text.loginDialog_validationTexts_password[i] }; }),
            { func: () => passwordBackendErrors.length === 0, errorText: () => passwordBackendErrors[0] },
        ], ls.icons.password, t => password = t, validationResults, 1, true);
        passwordInput.input.onFocusObservable.add(() => {
            passwordBackendErrors.splice(0, passwordBackendErrors.length);
            backendError = null;
            emailInput.validators.forEach(v => v.decorate());
        });
        signInStackPanel.addControl(passwordInput.inputStackPanel);
        signInButton.widthInPixels = this._mesh2D.m2px(ls.signUpSignInButton.width);
        signInButton.heightInPixels = this._mesh2D.m2px(ls.signUpSignInButton.height + ls.signUpSignInButton.padding);
        signInButton.paddingBottomInPixels = this._mesh2D.m2px(ls.signUpSignInButton.padding);
        signInButton.addControl(ObjectBuilder.createTextBlock3D(this._mesh2D, "loginDialogSignInButtonText", text.loginDialog_signIn, ls.signUpSignInButton.text));
        signInButton.onPointerClickObservable.add(async () => {
            if (Object.keys(validationResults).some((n) => validationResults[n].some(v => !v))) {
                emailInput.validators.forEach(v => v.decorate());
                passwordInput.validators.forEach(v => v.decorate());
                return;
            }
            this._tabControl.isVisible = false;
            if (spinnerScreen !== undefined)
                spinnerScreen.isVisible = true;
            const result = await Promise.race([callback(email, password), new Promise((success) => setTimeout(() => success({ status: "error", message: "Timeout" }), ls.timeout))]);
            if (spinnerScreen !== undefined)
                spinnerScreen.isVisible = false;
            if (isSuccessAuthCallbackResponse(result)) {
                successScreen.isVisible = true;
                setTimeout(() => {
                    this._close();
                    successScreen.isVisible = false;
                    this._tabControl.isVisible = true;
                    emailInput.input.text = "";
                    passwordInput.input.text = "";
                }, 3000);
            }
            else {
                if (result.errors) {
                    for (const e of result.errors.filter(e => e.field === "email"))
                        emailBackendErrors.push(e.error);
                    for (const e of result.errors.filter(e => e.field === "password"))
                        passwordBackendErrors.push(e.error);
                }
                backendError = result.message;
                this._tabControl.isVisible = true;
                emailInput.validators.forEach(v => v.decorate());
                passwordInput.validators.forEach(v => v.decorate());
            }
        });
        signInGrid.addControl(signInButton, 1);
        return signInGrid;
    }
    dispose() {
        this._mesh2D.dispose();
        this._keyboard.dispose();
    }
    get isVisible() {
        return this._mesh2D.mesh.isVisible;
    }
}
